// frontend/src/pages/BacktestCLIDiffusionPage.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import useDateRange from '../../hooks/useDateRange';
import useViewportCheck from '../../hooks/useViewportCheck'; 
import DateRangeControls from '../../components/DateRangeControls';
import Loading from '../../components/Loading';
import { createChartOptions } from '../../utils/chartOptions';
import {Typography, Card, Divider, Select, Space, Row, Col, Statistic, Tabs, Table, Collapse } from 'antd';
import { SettingOutlined, InfoCircleOutlined, CaretUpOutlined, CaretDownOutlined, BulbOutlined  } from '@ant-design/icons';
import dayjs from 'dayjs';
import ResponsiveTypography from '../../components/ResponsiveTypography';

const { Text, Paragraph } = Typography;
const { Title } = ResponsiveTypography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const ASSET_NAME_MAP = {
    "KOSPI200": '코스피200 (KOSPI200)',
    "KOSDAQ": '코스닥 (KOSDAQ)',
    
    "S&P500": 'S&P500',
    "NASDAQ100": '나스닥100 (NASDAQ100)',
    "Russell2000": '미국 소형주 2000 지수 (Russell 2000)',
    "DowJones": '다우존스 산업평균지수 (Dow Jones)',

    "Nikkei225": '니케이225 (Nikkei225)',
    "HangSeng": '항셍지수 (HangSeng)',
    "Shanghai_Composite": '상해종합지수 (Shanghai_composite)',

    "FTSE100" : '영국 (FTSE100)',
    "DAX": '독일 DAX (DAX)',
    "CAC40": '프랑스 CAC 40 (CAC40)',

    "TQQQ": '나스닥 3배 레버리지 ETF (TQQQ)',
    "UPRO": 'S&P500 3배 레버리지 ETF (UPRO)',
    "KODEX_Leveraged": '코스피 레버리지 ETF (KODEX 레버리지)',
    "KODEX_Inverse": '코스피 인버스 ETF (KODEX 인버스)',
    "KODEX_Semicon": '한국 반도체 ETF (KODEX 반도체)',

    "Gold" : '금 (Gold)',
    "Silver" : '은 (Silver)',
    "Copper" : '구리 (Copper)',

    "XLY": '경기소비재 섹터 ETF (XLY)',
    "XLU": '유틸리티 섹터 ETF (XLU)',
    "XLP": '필수소비재 섹터 ETF (XLP)',
    "XLF": '금융 섹터 ETF (XLF)',
    "XLE": '에너지 섹터 ETF (XLE)',
    "XLV": '헬스케어 섹터 ETF (XLV)',
    "XLI": '산업재 섹터 ETF (XLI)',
    "XLB": '소재 섹터 ETF (XLB)',
    "XLK": '기술 섹터 ETF (XLK)',
    "TLT": '미국 20년+ 장기국채 ETF (TLT)',

};

// --- 컴포넌트 섹션 ---

const StrategyDescription = () => (
    <div className="mb-8" style={{ padding: '0rem', marginBottom: '0.625rem' }}>
        <Title style={{ marginLeft: "0.625rem", marginRight: "0.625rem", marginBottom: '2.0rem', marginTop: '1.5rem' }} level={2}>
            OECD CLI Diffusion Index 기반 트레이딩 전략
        </Title>
        <Card size="small"  style={{ background: '#f8f9fa', marginTop: '1rem', marginBottom: '1.25rem' }}>
            <Title level={5} style={{ marginTop: 0 }}><BulbOutlined style={{ color: '#fae100' }}/> OECD CLI 데이터 갱신</Title>
            매달 업데이트되는 최신 CLI 지표는 OECD에서 발표하는 다음 날 <Text strong style={{ color: '#1890ff' }}>아래 차트</Text>에 반영됩니다.
        </Card>
        <Paragraph  style={{ marginTop: '1.25rem', marginLeft: "0.4375rem", marginRight: "0.4375rem", textAlign: 'justify', wordBreak: 'break-all' }}>
            G20 국가 중 GDP 대비 수출 비중이 가장 높은 **제조업 중심** 3개국(한국, 독일, 멕시코)의 CLI 월간 변화가 상승하는 비율을 의미하는 확산지수(Diffusion Index)에 따라 트레이딩하는 전략입니다.<br/>
            트레이딩 전략은 매우 단순합니다. 확산 지수가 50% 이상으로 오르면 롱 포지션 진입, 50% 미만으로 떨어지면 포지션 청산합니다.<br/>
            위 전략은 단순 장기투자 (Buy&Hold) 전략보다 수익 발생 확률이 높고 보유기간이 짧은 특징이 있기 때문에  <Text strong style={{ color: '#1890ff' }}>레버리지</Text>를 사용하는 것이 이론적으로 유리합니다.<br/>
        </Paragraph>
        
        <Collapse ghost style={{ marginLeft: "-0.625rem", marginRight: "-0.625rem" }}>
            <Panel header="백테스팅 세팅 보기" key="1">
            <Paragraph  style={{ marginTop: '0rem',  marginBottom: '0rem', marginLeft: "0.625rem", marginRight: "1.25rem", textalign: 'left', wordBreak: 'keep-all', padding : '0rem 0rem' }}>

                <Text type="secondary">
                <ol style={{ paddingLeft: '1.25rem' }}>
                    <li>
                    백테스팅에 사용된 데이터는 yfinance에서 제공되는 주가정보와 OECD에서 제공되는 공식 데이터를 기반으로 합니다.
                    </li>
                    <li>
                    OECD CLI 지표 발표일은 매달 상이합니다. 하지만 관례상 15일 전에 발표되었기 때문에 15일에 발표된다고 가정해 백테스팅을 진행했습니다.
                    </li>
                    <li>
                    거래비용은 0.25% 으로 설정했습니다.
                    </li>
                    <li>
                    '트레이딩 CAGR' 계산시 전체 트레이딩의 시작일과 종료일 기준으로 계산하였기 때문에, 종목을 실제로 보유하는 기간만을 기준으로 한 '보유 기간 CAGR'은 '트레이딩 CAGR'보다 높을 수 있습니다.
                    </li>
                </ol>
                </Text>
            </Paragraph>
            </Panel>
        </Collapse>

    </div>
);


// Helper function to format numbers for table alignment
const formatNumber = (num) => {
    if (typeof num !== 'number' || !isFinite(num)) return '-';
    return num.toFixed(1);
};

const BacktestCLIDiffusionPage = () => {
    // --- 수정: 통계 카드 글자 크기 조절을 위한 상수 선언 ---
    const isNarrow = useViewportCheck();
    const statisticTitleFontSize = isNarrow ? '0.8rem' : '0.9rem';
    const statisticValueFontSize = isNarrow ? '1.2rem' : '1.4rem';

    // State for selected assets, defaulting to KOSPI
    const [selectedStockMarket, setSelectedStockMarket] = useState('S&P500');

    // State for lists of options
    const [stockMarketList, setStockMarketList] = useState([]);

    // State for chart data and loading
    const [chartData, setChartData] = useState(null);
    const [tradingResults, setTradingResults] = useState([]);
    const [tradingStats, setTradingStats] = useState(null);
    const [annualReturns, setAnnualReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const chartRef = useRef(null);

    // Custom hook for date range management
    const { minDate, maxDate, viewStartDate, viewEndDate, handlePeriodChange, handleDateChange, isDateReady } = useDateRange(chartData?.stock_market_data, { dateColumn: 'Date', defaultPeriod: '5Y' });

    // Fetch lists for select boxes on component mount
    useEffect(() => {
        apiClient.get('/strategy-oecd-cli-di/list')
            .then(res => {
                setStockMarketList(res.data.stock_market_list.map(item => ({ 
                    value: item, 
                    label: ASSET_NAME_MAP[item] || item 
                })));
            })
            .catch(error => {
                console.error("Failed to fetch lists:", error);
            });
    }, []);

    // Fetch chart and trading data when selections change
    useEffect(() => {
        if (!stockMarketList.length) return;

        setLoading(true);
        const fetchData = async () => {
            try {
                const params = {
                    stock_market_asset: selectedStockMarket,
                };

                const [chartRes, tradingRes, statsRes, annualRes] = await Promise.all([
                    apiClient.get('/strategy-oecd-cli-di', { params }),
                    apiClient.get('/strategy-oecd-cli-di/trading-results', { params }),
                    apiClient.get('/strategy-oecd-cli-di/trading-statistics', { params }),
                    apiClient.get('/strategy-oecd-cli-di/annual-returns', { params })
                ]);

                setChartData(chartRes.data);
                const sortedTradingResults = tradingRes.data.sort((a, b) => new Date(b['Exit Date']) - new Date(a['Exit Date']));
                setTradingResults(sortedTradingResults);
                setTradingStats(statsRes.data[0]);
                const sortedAnnualReturns = annualRes.data.sort((a, b) => b.Year - a.Year);
                setAnnualReturns(sortedAnnualReturns);

            } catch (error) {
                console.error("Failed to fetch data:", error);
                setChartData(null);
                setTradingResults([]);
                setTradingStats(null);
                setAnnualReturns([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedStockMarket, stockMarketList]);

    // Function to generate chart options
    const getChartOption = useCallback(() => {
        if (!isDateReady || !chartData) {
            return {};
        }

        const stockMarketLabel = ASSET_NAME_MAP[selectedStockMarket] || selectedStockMarket;
        const oecdCliLabel = "CLI Diffusion Index";

        // 💡 [수정] oecdCliDataMap 생성 시 "diffusion_index" 컬럼을 사용합니다.
        const oecdCliDataMap = new Map(
            chartData.oecd_cli_di_data.map(d => [dayjs(d.Date).format('YYYY-MM-DD'), d['diffusion_index']])
        );

        const stockMarkPoints = [];
        const stockMarkLineData = [];
        const oecdMarkPoints = [];
        const oecdMarkLineData = [];

        if (tradingResults && tradingResults.length > 0) {
            tradingResults.forEach(trade => {
                const isProfit = trade['Return (%)'] > 0;
                const isHolding = trade['Holding'] === true;

                // 주식 차트의 markPoints 로직
                stockMarkPoints.push({
                    name: '진입',
                    coord: [trade['Entry Date'], trade['Entry Price']],
                    symbol: 'triangle',
                    symbolRotate: 0,
                    symbolSize: 10,
                    itemStyle: { color: '#28a745' },
                });

                if (isHolding) {
                    stockMarkPoints.push({
                        name: '보유 중',
                        coord: [trade['Exit Date'], trade['Exit Price']],
                        symbol: 'triangle',
                        symbolRotate: 180,
                        symbolSize: 10,
                        itemStyle: { color: '#505050' },
                        label: {
                            show: true,
                            formatter: `보유 중\n ${trade['Return (%)'].toFixed(1)}% `,
                            position: isProfit ? 'top' : 'bottom',
                            distance: 15,
                            fontSize: 12,
                            color: 'white',
                            backgroundColor: '#505050',
                            padding: [4, 2, 2, 2],
                            borderRadius: 4,
                        }
                    });
                } else {
                    stockMarkPoints.push({
                        name: '청산',
                        coord: [trade['Exit Date'], trade['Exit Price']],
                        symbol: 'triangle',
                        symbolRotate: 180,
                        symbolSize: 10,
                        itemStyle: { color: '#dc3545' },
                        label: {
                            show: true,
                            formatter: `${trade['Return (%)'].toFixed(1)}%`,
                            position: isProfit ? 'top' : 'bottom',
                            distance: 15,
                            fontSize: 12,
                            color:  'white',
                            backgroundColor: isProfit ? '#28a745' : '#dc3545',
                            padding: [4, 2, 2, 2],
                            borderRadius: 4,
                        }
                    });
                }
                
                stockMarkLineData.push([
                    {
                        coord: [trade['Entry Date'], trade['Entry Price']],
                        symbol: 'none'
                    },
                    {
                        coord: [trade['Exit Date'], trade['Exit Price']],
                        symbol: 'none',
                        lineStyle: {
                            color: '#505050',
                            width: 2,
                            type: 'dashed'
                        }
                    }
                ]);

                const EntryDate = trade['Entry Date'];
                const ExitDate = trade['Exit Date'];
                const oecdEntryValue = oecdCliDataMap.get(EntryDate);
                const oecdExitValue = oecdCliDataMap.get(ExitDate);

                // OECD 차트의 markPoints 및 markLine 로직
                if (oecdEntryValue !== undefined) {
                    oecdMarkPoints.push({
                        name: 'Entry',
                        coord: [EntryDate, oecdEntryValue],
                        symbol: 'triangle',
                        symbolRotate: 0,
                        symbolSize: 8,
                        itemStyle: { color: '#28a745' },
                        label: {
                            show: true,
                            formatter: '{b}',
                            position: 'top',
                            distance: 5,
                            color: '#28a745',
                            fontSize: 10,
                        }
                    });
                }

                if (oecdExitValue !== undefined) {
                    oecdMarkPoints.push({
                        name: isHolding ? 'holding' : 'Exit',
                        coord: [ExitDate, oecdExitValue],
                        symbol: 'triangle',
                        symbolRotate: 180,
                        symbolSize: 8,
                        itemStyle: { color: isHolding ? '#505050' : '#dc3545' },
                        label: {
                            show: true,
                            formatter: '{b}',
                            position: 'bottom',
                            distance: 5,
                            color: isHolding ? '#505050' : '#dc3545',
                            fontSize: 10,
                        }
                    });
                }
                
                if (oecdEntryValue !== undefined && oecdExitValue !== undefined) {
                    oecdMarkLineData.push([
                        {
                            coord: [EntryDate, oecdEntryValue],
                            symbol: 'none'
                        },
                        {
                            coord: [ExitDate, oecdExitValue],
                            symbol: 'none',
                            lineStyle: {
                                color: '#505050',
                                width: 2,
                                type: 'dashed'
                            }
                        }
                    ]);
                }
            });
        }

        const finalOptions = {
            animation: true,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                formatter: (params) => {
                    if (!params || params.length === 0) {
                        return;
                    }
                    
                    const date = dayjs(params[0].axisValue).format('YYYY-MM-DD');
                    let tooltipHtml = `${date}<br/>`;

                    params.forEach(param => {
                        const seriesName = param.seriesName;
                        const value = param.value[1]; 
                        let formattedValue;

                        if (value == null) {
                            formattedValue = 'N/A';
                        } else {
                            if (seriesName === oecdCliLabel) {
                                // Diffusion Index 값은 소수점 1자리로 표시
                                formattedValue = value.toFixed(1);
                            } else {
                                formattedValue = value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                            }
                        }
                        
                        tooltipHtml += `${param.marker} ${seriesName}: <strong>${formattedValue}</strong><br/>`;
                    });

                    return tooltipHtml;
                }
            },
            legend: { data: [stockMarketLabel, oecdCliLabel], top: '5%' },

            grid: [ 
                { left: '65rem', right: '40rem', top: '15%', height: '40%'}, 
                { left: '65rem', right: '40rem', top: '65%', height: '23%' } 
            ],
            xAxis: [ { type: 'time', gridIndex: 0, axisLabel: { show: false }, boundaryGap: false }, { type: 'time', gridIndex: 1, boundaryGap: false } ],
            yAxis: [
                { name: "종목 가격", type: 'value', gridIndex: 0, scale: true, log: true, splitLine: { show: true },  axisLine: { show: true } },
                { name: "Diffusion Index", type: 'value', gridIndex: 1, scale: true, splitLine: { show: true }, axisLine: { show: true } }
            ],
            series: [
                {
                    name: stockMarketLabel,
                    type: 'line',
                    data: chartData.stock_market_data.map(d => [d.Date, d[selectedStockMarket]]),
                    xAxisIndex: 0, yAxisIndex: 0, showSymbol: false,
                    markPoint: { data: stockMarkPoints, silent: false },
                    markLine: {
                        silent: true,
                        symbol: ['none', 'none'],
                        data: stockMarkLineData
                    }
                },
                {
                    name: oecdCliLabel, 
                    type: 'line',
                    color: 'orange',
                    // 💡 [수정] "diffusion_index" 컬럼을 사용합니다.
                    data: chartData.oecd_cli_di_data.map(d => [d.Date, d['diffusion_index']]),
                    xAxisIndex: 1, yAxisIndex: 1, showSymbol: false,
                    markPoint: { data: oecdMarkPoints, silent: false },
                    markLine: {
                        silent: true,
                        symbol: ['none', 'none'],
                        data: oecdMarkLineData
                    }
                }
            ],
            dataZoom: [ { type: 'slider', show: true, xAxisIndex: [0, 1], bottom: '2%', startValue: viewStartDate?.toISOString(), endValue: viewEndDate?.toISOString() }, { type: 'inside', xAxisIndex: [0, 1] } ]
        };
        return createChartOptions(finalOptions);
    }, [isDateReady, chartData, viewStartDate, viewEndDate, selectedStockMarket, tradingResults]);
    

    const renderColoredCell = (text) => {
        if (typeof text !== 'number') return '-';
        const color = text > 0 ? 'green' : text < 0 ? 'red' : 'black';
        return (
            <span style={{ color: color }}>
                {formatNumber(text)}%
            </span>
        );
    };

    const renderCell = (text) => {
        if (typeof text !== 'number') return '-';
        return (
            <span style={{}}>
                {formatNumber(text)}%
            </span>
        );
    };


    const formatPrice = (text) => {
        const number = parseFloat(String(text).replace(/,/g, ''), 10);
        return isNaN(number) ? text : number.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });
    };


    const tradingHistoryColumns = [

        { title: '수익률', dataIndex: 'Return (%)', key: 'return', align: 'right',render: renderColoredCell},
        { title: '진입일', dataIndex: 'Entry Date', key: 'EntryDate', render: (text) => dayjs(text).format('YYYY-MM-DD'), align: 'right'},
        { title: '진입가격', dataIndex: 'Entry Price', key: 'EntryPrice', render: formatPrice, align: 'right'},
        { title: '청산일', dataIndex: 'Exit Date', key: 'ExitDate', render: (text) => dayjs(text).format('YYYY-MM-DD'), align: 'right' },
        { title: '청산가격', dataIndex: 'Exit Price', key: 'ExitPrice',render: formatPrice, align: 'right' },

        { title: 'Drawdown 발생일', dataIndex: 'Trough Date', key: 'troughDate', render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-', align: 'right'},
        { title: '거래별 Drawdown', dataIndex: 'Drawdown (%)', key: 'drawdown', render: (text) => `${formatNumber(text)}%`, align: 'right' },
    ];

    const annualPerformanceColumns = [
        { title: '연도', dataIndex: 'Year', key: 'year', align: 'center' },
        { 
            title: '전략 수익률', 
            dataIndex: 'Annual Return (%)', 
            key: 'annualReturn', 
            align: 'right',
            render: renderColoredCell
        },
        { 
            title: '단순보유 수익률', 
            dataIndex: 'Benchmark Return (%)', 
            key: 'benchmarkReturn', 
            align: 'right',
            render: renderColoredCell
        },
        {
            title: '초과 수익률',
            key: 'excessReturn',
            align: 'right',
            render: (text, record) => {
                const annualReturn = record['Annual Return (%)'];
                const benchmarkReturn = record['Benchmark Return (%)'];
                if (typeof annualReturn !== 'number' || typeof benchmarkReturn !== 'number') {
                    return '-';
                }
                const excessReturn = annualReturn - benchmarkReturn;
                return renderColoredCell(excessReturn);
            }
        },
        { 
            title: '전략 Drawdown', 
            dataIndex: 'Annual Drawdown (%)', 
            key: 'annualDrawdown', 
            align: 'right',
            render: renderCell
        },
        { 
            title: '밴치마크 Drawdown', 
            dataIndex: 'Benchmark Drawdown (%)', 
            key: 'benchmarkDrawdown', 
            align: 'right',
            render: renderCell
        },

    ];

    const paginationConfig = {
        defaultPageSize: 10, // 기본적으로 보여줄 줄 수
        showSizeChanger: true, // 줄 수 변경 옵션 표시 여부 (이게 핵심입니다!)
        pageSizeOptions: ['10', '20', '50', '100'], // 선택 가능한 줄 수 옵션
        position: ['bottomCenter'], // 페이지네이션 위치 (선택사항)
    };

    if (loading || !chartData) {
        return (
            <Loading />
        );
    }

    return (
        <div className="content-box">
            <StrategyDescription />

            {tradingStats && (
                <Card style={{ marginBottom: '1rem' }} bodyStyle={{ padding: '0.3125rem 0.3125rem' }}>
                    <Row gutter={[5, 10]}>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic
                                title={<span style={{ fontSize: statisticTitleFontSize }}>트레이딩 누적 수익률</span>}
                                value={tradingStats['Cumulative Return (%)']} 
                                precision={0} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>트레이딩 CAGR</span>}
                                value={tradingStats['CAGR (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>보유 기간 CAGR</span>}
                                value={tradingStats['Holding CAGR (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>단순 장기투자 CAGR</span>}
                                value={tradingStats['BNP CAGR (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>트레이딩 수익 확률</span>}
                                value={tradingStats['Win Rate (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>전략 MDD</span>}
                                value={tradingStats['Strategy MDD (%)']} 
                                precision={1} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>T-통계량</span>}
                                value={tradingStats['T-statistic']} 
                                precision={1} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>P-값(%)</span>}
                                value={tradingStats['P-value (%)']} 
                                precision={2} 
                                suffix="%" 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>샤프 지수</span>}
                                value={tradingStats['Sharpe Ratio']} 
                                precision={2} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>총 거래 횟수</span>}
                                value={tradingStats['Total Trades']} 
                                suffix="회" 
                                precision={0} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>총 투자 기간</span>}
                                value={tradingStats['Total Period (Years)']} 
                                suffix="년" 
                                precision={1} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                        <Col xs={12} sm={6} style={{ textAlign: 'center' }}>
                            <Statistic 
                                title={<span style={{ fontSize: statisticTitleFontSize }}>총 보유 기간</span>}
                                value={tradingStats['Total Holding Period (Years)']} 
                                suffix="년" 
                                precision={1} 
                                valueStyle={{ fontSize: statisticValueFontSize }} 
                            />
                        </Col>
                    </Row>
                </Card>
            )}

            <Card style={{ marginTop: '1rem' }} bodyStyle={{ padding: '0.625rem' }}>
                <Row gutter={[10, 10]} align="middle">
                    <Col xs={24} sm={24}> 
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong><SettingOutlined /> 트레이딩 종목 </Text>
                            <Select showSearch value={selectedStockMarket} onChange={setSelectedStockMarket} style={{ width: '100%' }} options={stockMarketList}
                                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card style={{ marginTop: '0.625rem', padding: '0rem', marginLeft: '0rem', marginRight: '0rem' }} bodyStyle={{ padding: '0.625rem' }}>
                <Title level={4}>진입 / 청산 기록 차트</Title>
                <Divider style={{ margin: 0 }}/>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.625rem', marginBottom : '0.625rem', marginLeft: '0.625rem', gap: '0.625rem' }}>
                    <Text strong style={{ fontSize: '1.1em' }}><CaretUpOutlined style={{ color: '#28a745', fontSize: '1.2em', verticalAlign: 'middle', marginRight: '0.25rem' }} />진입 </Text>
                    <Text strong style={{ fontSize: '1.1em' }}><CaretDownOutlined style={{ color: '#dc3545', fontSize: '1.2em', verticalAlign: 'middle', marginRight: '0.25rem' }} />청산 </Text>
                    <Text strong style={{ fontSize: '1.1em' }}><CaretDownOutlined  style={{ color: '#505050', fontSize: '1.2em', verticalAlign: 'middle', marginRight: '0.25rem' }} /> 보유 중 </Text>
                </div>

                <ReactECharts ref={chartRef} option={getChartOption()} style={{ height: '37.5rem' }} showLoading={loading} notMerge={true} />
                
                <Divider />
                <DateRangeControls minDate={minDate} maxDate={maxDate} viewStartDate={viewStartDate} viewEndDate={viewEndDate} onPeriodChange={handlePeriodChange} onDateChange={handleDateChange} />
            </Card>

            <Card style={{ marginTop: '0.75rem' }}>
                <Tabs defaultActiveKey="1">
                    <TabPane tab="거래 기록" key="1">
                        <Table dataSource={tradingResults} columns={tradingHistoryColumns} rowKey={(record, index) => index} size="small" scroll={{ x: 'max-content' }} pagination={paginationConfig} />
                    </TabPane>
                    <TabPane tab="연도별 성과 비교" key="2">
                        <Table dataSource={annualReturns} columns={annualPerformanceColumns} rowKey="Year" size="small" scroll={{ x: 'max-content' }} pagination={paginationConfig} />
                    </TabPane>
                </Tabs>
            </Card>

            <Card size="small"  style={{ background: '#f8f9fa', marginTop: '1rem', marginBottom: '1.25rem' }}>
                <Title level={5}><InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }}/> 주의사항</Title>
                <Paragraph  style={{ marginTop: '1.25rem', marginLeft: "0.4375rem", marginRight: "0.4375rem", textalign: 'left', wordBreak: 'keep-all' }}>
                    OECD가 공식홈페이지에서 발표하는 CLI 지표는 통계적 안정성을 위해 <Text strong style={{ color: '#1890ff' }}>"사후 수정"</Text>됩니다. 따라서 정확한 백테스팅을 위해서는 <Text strong style={{ color: '#1890ff' }}> 수정되지 않은</Text> 각 발표 시점의 마지막 데이터를 사용해야 합니다.
                </Paragraph>
                <Paragraph style={{ marginLeft: "1.25rem", marginTop: '-0.3125rem' }}>
                    🔗 <a href="https://data-explorer.oecd.org/vis?df[ds]=dsDisseminateFinalDMZ&df[id]=DSD_STES_REVISIONS%40DF_STES_REVISIONS&df[ag]=OECD.SDD.STES&df[vs]=4.0&dq=KOR.M.LI_AA...&pd=2021-01%2C&to[TIME_PERIOD]=false&vw=tb" target="_blank" rel="noopener noreferrer"> OECD 수정 전 데이터 링크(클릭!)</a>
                </Paragraph>
            </Card>
        </div>
    );
};

export default BacktestCLIDiffusionPage;